import React, { useEffect, useMemo, useState } from "react";
import { Upload, Eye, PowerOff, Power, ShieldCheck, X, AlertTriangle, CheckCircle2, Plane, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtPaxStr, fmtRevStr, localizeCountry, deriveAdiMarketKey } from "@/lib/marketFormat";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const STATUS_STYLES = {
  ready: "bg-[#ECF3EC] text-[#2E7D46] border-[#CBE3D1]",
  loading: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  disabled: "bg-slate-100 text-slate-500 border-slate-200",
};

const StatusBadge = ({ status }) => (
  <span data-testid={`status-badge-${status}`}
    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold capitalize ${STATUS_STYLES[status] || STATUS_STYLES.disabled}`}>
    {status}
  </span>
);

const fmtDate = (v) => (v ? String(v).slice(0, 16).replace("T", " ") : "—");
const frNum0 = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 2 }));

export default function AdminMarketData() {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ source: "", market: "", status: "" });
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/market-data/admin/datasets");
      setDatasets(data.datasets || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => datasets.filter((d) =>
    (!filters.source || d.source === filters.source) &&
    (!filters.market || d.market_key === filters.market) &&
    (!filters.status || d.status === filters.status)), [datasets, filters]);

  const sources = [...new Set(datasets.map((d) => d.source).filter(Boolean))];
  const markets = [...new Set(datasets.map((d) => d.market_key).filter(Boolean))];

  const toggleStatus = async (d) => {
    const action = d.status === "ready" ? "disable" : "enable";
    try {
      await api.post(`/market-data/admin/datasets/${encodeURIComponent(d.dataset_id)}/${action}`);
      toast.success(action === "disable" ? "Dataset désactivé" : "Dataset réactivé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openDetail = async (d) => {
    try {
      const { data } = await api.get(`/market-data/admin/datasets/${encodeURIComponent(d.dataset_id)}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteDataset = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/market-data/admin/datasets/${encodeURIComponent(deleteTarget.dataset_id)}`, {
        data: { confirmation: deleteTarget.dataset_id },
      });
      toast.success("Dataset supprimé définitivement");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail === "REFERENCE_DATASET_PROTECTED"
        ? "Ce dataset système est protégé et ne peut pas être supprimé."
        : formatApiError(detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="admin-market-data" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#17201A]">Lodge Market Data</h1>
          <p className="text-sm text-slate-500">Datasets Sabre ADI de référence — importés et validés par Lodge & Co.</p>
        </div>
        <button data-testid="open-import-btn" onClick={() => setShowImport(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#254a35]">
          <Upload size={16} /> Importer un dataset Sabre ADI
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {[["source", "Toutes les sources", sources], ["market", "Tous les marchés", markets],
          ["status", "Tous les statuts", ["ready", "loading", "failed", "disabled"]]].map(([key, label, opts]) => (
          <select key={key} data-testid={`filter-${key}`} value={filters[key]}
            onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
            <option value="">{label}</option>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <SelectableTable rows={filtered} rowId={(d) => d.dataset_id} filename="market-data-datasets.csv"
          ariaLabel="Datasets Market Data — sélection et défilement"
          csvColumns={[
            { label: "Marché", get: (d) => `${localizeCountry(d.origin)} → ${localizeCountry(d.destination)}` },
            { label: "Code marché", get: (d) => d.market_key },
            { label: "Période", get: (d) => `${d.period_start} → ${d.period_end}` },
            { label: "Granularité", get: (d) => d.granularity },
            { label: "Statut", get: (d) => d.status },
            { label: "Lignes", get: (d) => d.row_count ?? "" },
            { label: "Passagers", get: (d) => d.total_passengers ?? "" },
            { label: "Revenus aériens", get: (d) => d.total_base_revenue ?? "" },
            { label: "Tarif moyen", get: (d) => d.weighted_avg_fare ?? "" },
            { label: "Créé / Validé le", get: (d) => fmtDate(d.validated_at || d.created_at) },
          ]}>
          <table className="w-full min-w-[760px] text-sm" data-testid="datasets-table">
          <thead className="bg-[#FAFAF6] text-left text-[11px] uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-3 py-3 font-semibold"><SelectAllHeaderCheckbox /></th>
              {["Marché", "Code marché", "Période", "Granularité", "Statut", "Lignes", "Passagers", "Revenus aériens", "Tarif moyen", "Créé / Validé le", "Actions"].map((h) => (
                <th key={h} className="px-3 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">Chargement…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400">Aucun dataset.</td></tr>}
            {filtered.map((d) => (
              <tr key={d.dataset_id} data-testid={`dataset-row-${d.market_key}`} className="hover:bg-slate-50/60">
                <td className="px-3 py-3"><RowCheckbox id={d.dataset_id} label={`Sélectionner ${d.market_key}`} /></td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2 font-medium text-[#17201A]">
                    {localizeCountry(d.origin)} → {localizeCountry(d.destination)}
                    {d.protected && <ShieldCheck size={13} className="text-[#3E7256]" title="Dataset de référence protégé" />}
                  </div>
                </td>
                <td className="px-3 py-3 font-mono text-xs text-slate-500">{d.market_key}</td>
                <td className="px-3 py-3 text-slate-600">{d.period_start} → {d.period_end}</td>
                <td className="px-3 py-3 text-slate-500">{d.granularity}</td>
                <td className="px-3 py-3"><StatusBadge status={d.status} /></td>
                <td className="px-3 py-3 tabular-nums text-slate-600">{d.row_count ?? "—"}</td>
                <td className="px-3 py-3 tabular-nums text-slate-600">{d.total_passengers ? fmtPaxStr(d.total_passengers) : "—"}</td>
                <td className="px-3 py-3 tabular-nums text-slate-600">{d.total_base_revenue ? fmtRevStr(d.total_base_revenue) : "—"}</td>
                <td className="px-3 py-3 tabular-nums text-slate-600">{d.weighted_avg_fare ? `${frNum0(d.weighted_avg_fare)} $` : "—"}</td>
                <td className="px-3 py-3 text-xs text-slate-400">{fmtDate(d.validated_at || d.created_at)}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`view-${d.market_key}`} onClick={() => openDetail(d)} title="Voir détails"
                      className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100"><Eye size={15} /></button>
                    {!d.protected && (d.status === "ready" || d.status === "disabled") && (
                      <button data-testid={`toggle-${d.market_key}`} onClick={() => toggleStatus(d)}
                        title={d.status === "ready" ? "Désactiver" : "Réactiver"}
                        className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-100">
                        {d.status === "ready" ? <PowerOff size={15} /> : <Power size={15} />}
                      </button>
                    )}
                    {!d.protected && d.status !== "loading" && (
                      <button data-testid={`delete-${d.market_key}`} onClick={() => setDeleteTarget(d)}
                        title="Supprimer définitivement"
                        className="rounded-md border border-rose-200 p-1.5 text-rose-600 hover:bg-rose-50">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </SelectableTable>
      </div>

      {detail && <DetailDialog detail={detail} onClose={() => setDetail(null)} />}
      {deleteTarget && (
        <DeleteDatasetDialog dataset={deleteTarget} deleting={deleting}
          onCancel={() => !deleting && setDeleteTarget(null)} onConfirm={deleteDataset} />
      )}
      {showImport && <ImportDialog onClose={() => setShowImport(false)} onPublished={() => { setShowImport(false); load(); setRefreshKey((k) => k + 1); }} />}
      <MappingsSection refreshKey={refreshKey} />
    </div>
  );
}

const DeleteDatasetDialog = ({ dataset, deleting, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
    data-testid="delete-dataset-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-dataset-title">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      <div className="flex items-start gap-3">
        <span className="rounded-full bg-rose-50 p-2 text-rose-600"><AlertTriangle size={20} /></span>
        <div>
          <h4 id="delete-dataset-title" className="font-heading text-base font-bold text-[#17201A]">
            Supprimer définitivement ce dataset ?
          </h4>
          <p className="mt-2 text-sm text-slate-600">
            Le dataset et toutes ses lignes seront supprimés. Cette action est irréversible.
          </p>
        </div>
      </div>
      <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg bg-slate-50 p-4 text-sm">
        <dt className="text-slate-500">Marché</dt>
        <dd className="font-semibold text-slate-800">{localizeCountry(dataset.origin)} → {localizeCountry(dataset.destination)}</dd>
        <dt className="text-slate-500">Code</dt>
        <dd className="font-mono text-xs text-slate-700">{dataset.market_key}</dd>
        <dt className="text-slate-500">Période</dt>
        <dd className="text-slate-700">{dataset.period_start} → {dataset.period_end}</dd>
        <dt className="text-slate-500">Lignes</dt>
        <dd className="tabular-nums text-slate-700">{dataset.row_count ?? "—"}</dd>
      </dl>
      <div className="mt-6 flex justify-end gap-2">
        <button data-testid="delete-dataset-cancel" disabled={deleting} onClick={onCancel}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-50">
          Annuler
        </button>
        <button data-testid="delete-dataset-confirm" disabled={deleting} onClick={onConfirm}
          className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
          {deleting ? "Suppression…" : "Supprimer définitivement"}
        </button>
      </div>
    </div>
  </div>
);

function MappingsSection({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/market-data/admin/column-mappings"); setRows(data.mappings || []); }
    catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, [refreshKey]);
  const disable = async (m) => {
    try {
      await api.post("/market-data/admin/column-mappings/disable", { normalized_source_header: m.normalized_source_header });
      toast.success("Mapping désactivé"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="mappings-section" className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-3 font-heading text-base font-bold text-[#17201A]">Mappings Sabre</h2>
      {rows.length === 0 && (
        <p data-testid="mappings-empty" className="text-sm text-slate-500">
          Aucun mapping personnalisé enregistré. Les mappings apparaîtront ici lorsqu'une nouvelle colonne Sabre aura été validée.
        </p>
      )}
      <ul className="divide-y divide-slate-100 text-sm">
        {rows.map((m) => (
          <li key={m.normalized_source_header} data-testid={`mapping-${m.normalized_source_header}`} className="flex items-center justify-between py-2">
            <span className="text-slate-700"><span className="font-medium">{m.original_source_header}</span> → {m.canonical_field}
              <span className="ml-2 text-xs text-slate-400">· {m.usage_count || 0} utilisation(s)</span></span>
            <span className="flex items-center gap-3">
              <StatusBadge status={m.status} />
              {m.status === "validated" && (
                <button data-testid={`disable-mapping-${m.normalized_source_header}`} onClick={() => disable(m)}
                  className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">Désactiver</button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const Kpi = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className="font-mono text-lg font-semibold text-[#1E3D2B]">{value}</p>
  </div>
);

const TopList = ({ title, items, kind }) => (
  <div>
    <p className="mb-2 text-xs font-semibold text-slate-500">{title}</p>
    <ul className="space-y-1.5" data-testid={`top-${kind}`}>
      {(items || []).map((a) => (
        <li key={a.rank} className="flex justify-between text-sm">
          <span className="truncate text-slate-700">{a.rank}. {a.name}</span>
          <span className="ml-3 flex shrink-0 items-center gap-1 font-mono text-xs text-[#1E3D2B]">
            {kind === "pax" ? fmtPaxStr(a.raw) : fmtRevStr(a.raw)}
            <span
              className="text-slate-400"
              title={kind === "pax"
                ? "Part des passagers — calcul Lodge (passagers compagnie ÷ passagers marché)."
                : "Part du revenu — calcul Lodge (revenus compagnie ÷ revenus marché)."}
            >· {a.share}%</span>
            {a.adi_airline_share != null && (
              <span
                data-testid={`top-${kind}-adi-${a.rank}`}
                className="rounded bg-[#ECF3EC] px-1 py-0.5 text-[10px] font-semibold text-[#3E7256]"
                title="Part de marché ADI (fournisseur, source Sabre), conservée telle quelle. Mesure distincte des parts passagers/revenus calculées par Lodge."
              >ADI {a.adi_airline_share}%</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const Metrics = ({ m }) => (
  <>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Kpi label="Lignes" value={m.row_count} />
      <Kpi label="Passagers" value={fmtPaxStr(m.total_passengers)} />
      <Kpi label="Base Revenue" value={fmtRevStr(m.total_base_revenue)} />
      <Kpi label="Tarif pondéré" value={`${frNum0(m.weighted_avg_fare)} $`} />
    </div>
    <div className="grid gap-6 sm:grid-cols-2">
      <TopList title="Top 5 compagnies — Passagers" items={m.topAirlinesByPassengers} kind="pax" />
      <TopList title="Top 5 compagnies — Revenus" items={m.topAirlinesByRevenue} kind="rev" />
    </div>
  </>
);

const Shell = ({ title, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
    <div className={`my-8 w-full ${wide ? "max-w-3xl" : "max-w-xl"} rounded-2xl bg-white shadow-xl`}>
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <h3 className="font-heading text-base font-bold text-[#17201A]">{title}</h3>
        <button data-testid="dialog-close" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
      </div>
      <div className="space-y-5 px-6 py-5">{children}</div>
    </div>
  </div>
);

const DetailDialog = ({ detail, onClose }) => {
  const d = detail.dataset;
  return (
    <Shell title={`${localizeCountry(d.origin)} → ${localizeCountry(d.destination)}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
        {[["Source", d.source], ["Market Key", d.market_key], ["Dataset ID", d.dataset_id],
          ["Period", `${d.period_start} → ${d.period_end}`], ["Granularité", d.granularity],
          ["Destination", d.destination_implicit ? "Implicite" : "Explicite"],
          ["Statut", d.status], ["Fichier source", d.source_filename || "—"],
          ["Validé par", d.validated_by || "—"]].map(([k, v]) => (
          <div key={k}><p className="text-[11px] uppercase text-slate-400">{k}</p><p className="break-all font-medium text-slate-700">{v}</p></div>
        ))}
      </div>
      {detail.metrics && detail.metrics.row_count > 0
        ? <Metrics m={detail.metrics} />
        : <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-500">Aucune donnée servie (statut {d.status}).</p>}
    </Shell>
  );
};

function ImportDialog({ onClose, onPublished }) {
  const [step, setStep] = useState("select"); // select | preview
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState(null); // analysis
  const [meta, setMeta] = useState({ market_key: "", period_start: "", period_end: "", granularity: "aggregate", destination: "" });
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [overrides, setOverrides] = useState({}); // {original_header: canonical | "__extra__"}

  const analyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/market-data/admin/imports/analyze", fd);
      setA(data);
      const needsDestination = data.needs_confirmation?.destination ?? data.destination_implicit;
      const destForKey = needsDestination ? "" : (data.destination || "");
      setMeta((m) => ({
        ...m,
        market_key: deriveAdiMarketKey(data.origin, destForKey, data.destination_iata) || "",
        period_start: data.proposed_period_start || "",
        period_end: data.proposed_period_end || "",
        granularity: data.proposed_granularity || "aggregate",
        destination: needsDestination ? "" : (data.destination || ""),
      }));
      setStep("preview");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const datasetId = useMemo(() => {
    const { market_key, period_start, period_end, granularity } = meta;
    if (!market_key || !period_start || !period_end || !granularity) return "—";
    return `sabre_adi|${market_key}|${period_start}|${period_end}|${granularity}`.toLowerCase().replace(/ /g, "_");
  }, [meta]);

  const destinationNeedsConfirmation = Boolean(
    a?.needs_confirmation?.destination ?? a?.destination_implicit
  );
  const metaMissing = !meta.market_key || !meta.period_start || !meta.period_end || !meta.granularity ||
    (destinationNeedsConfirmation && !meta.destination);

  const publish = async (replace = false) => {
    setBusy(true);
    try {
      await api.post("/market-data/admin/imports/publish", {
        upload_id: a.upload_id, market_key: meta.market_key.toUpperCase(),
        period_start: meta.period_start, period_end: meta.period_end,
        granularity: meta.granularity, source: "Sabre ADI",
        destination: destinationNeedsConfirmation ? meta.destination : null, replace,
        column_overrides: overrides,
      });
      toast.success("Dataset publié et disponible");
      onPublished();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = e.response?.status;
      if (detail === "REFERENCE_DATASET_PROTECTED") {
        toast.error("Ce dataset de référence est protégé et ne peut pas être remplacé depuis cette interface.");
      } else if (code === 409) {
        setConfirmReplace(true);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Importer un dataset Sabre ADI" onClose={onClose} wide>
      {step === "select" && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">Sélectionnez un fichier CSV Sabre ADI. Il sera d'abord <strong>analysé</strong>, puis vous pourrez le prévisualiser avant de le publier.</p>
          <input data-testid="import-file-input" type="file" accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full rounded-lg border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-600" />
          <div className="flex justify-end">
            <button data-testid="analyze-btn" disabled={!file || busy} onClick={analyze}
              className="rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
              {busy ? "Analyse…" : "Analyser"}
            </button>
          </div>
        </div>
      )}

      {step === "preview" && a && (
        <div className="space-y-5" data-testid="import-preview">
          <div className="rounded-lg bg-[#FAFAF6] px-4 py-3 text-sm">
            <p className="font-medium text-slate-700">{a.filename} · {a.row_count} lignes</p>
            <p className="text-xs text-slate-500">Origine détectée : <strong>{a.origin || "—"}</strong> · Destination : <strong>{a.destination || "à confirmer"}</strong>{a.destination_iata ? ` (${a.destination_iata})` : ""} ({a.destination_implicit ? "implicite" : "explicite"})</p>
          </div>

          {a.warnings?.length > 0 && (
            <div data-testid="import-warnings" className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {a.warnings.map((w, i) => <p key={i} className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {w}</p>)}
            </div>
          )}

          {a.unknown_columns?.length > 0 && (
            <div data-testid="unknown-columns" className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-sm font-semibold text-slate-700">Colonnes à confirmer</p>
              {a.unknown_columns.map((u) => (
                <div key={u.header} data-testid={`unknown-${u.normalized}`} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2 first:border-0 first:pt-0">
                  <div className="text-sm">
                    <span className="font-medium text-slate-700">{u.header}</span>
                    <span className="ml-2 text-xs text-slate-400">type {u.detected_type} · ex. {u.samples.slice(0,2).join(", ")}</span>
                    {u.suggestion && <span className="ml-2 rounded-full bg-[#ECF3EC] px-2 py-0.5 text-[11px] font-semibold text-[#2E7D46]">Suggestion : {a.canonical_fields.find((c)=>c.value===u.suggestion)?.label}</span>}
                  </div>
                  <select data-testid={`map-${u.normalized}`} value={overrides[u.header] || ""}
                    onChange={(e) => setOverrides((o) => ({ ...o, [u.header]: e.target.value }))}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm">
                    <option value="">— Choisir —</option>
                    {a.canonical_fields.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    <option value="__extra__">Dimension additionnelle</option>
                  </select>
                </div>
              ))}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block text-xs font-semibold text-slate-500">Code marché *</span>
              <input data-testid="meta-market-key" value={meta.market_key} placeholder="ex. FR-KE"
                onChange={(e) => setMeta((m) => ({ ...m, market_key: e.target.value.toUpperCase() }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2" />
              {!meta.market_key
                ? <span className="mt-1 block text-[11px] text-amber-600">Code non déterminé automatiquement (pays inconnu) — saisie manuelle requise.</span>
                : <span className="mt-1 block text-[11px] text-[#3E7256]">Généré automatiquement depuis l'origine et la destination — modifiable.</span>}
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-semibold text-slate-500">Granularité *</span>
              <input data-testid="meta-granularity" value={meta.granularity}
                onChange={(e) => setMeta((m) => ({ ...m, granularity: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-semibold text-slate-500">Period Start * (YYYY-MM)</span>
              <input data-testid="meta-period-start" value={meta.period_start} placeholder="2025-08"
                onChange={(e) => setMeta((m) => ({ ...m, period_start: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-semibold text-slate-500">Period End * (YYYY-MM)</span>
              <input data-testid="meta-period-end" value={meta.period_end} placeholder="2026-07"
                onChange={(e) => setMeta((m) => ({ ...m, period_end: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2" />
            </label>
            {destinationNeedsConfirmation && (
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Pays de destination * (à confirmer)</span>
                <input data-testid="meta-destination" value={meta.destination} placeholder="ex. Mauritius"
                  onChange={(e) => setMeta((m) => ({ ...m, destination: e.target.value,
                    market_key: deriveAdiMarketKey(a.origin, e.target.value, a.destination_iata) || m.market_key }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 px-4 py-2 text-xs">
            <span className="text-slate-400">Dataset ID : </span><span data-testid="preview-dataset-id" className="break-all font-mono text-slate-600">{datasetId}</span>
          </div>

          {a.metrics
            ? <Metrics m={a.metrics} />
            : <p className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">Le fichier n'a pas pu être analysé (colonnes critiques manquantes ou ambiguës). Publication impossible.</p>}

          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <button onClick={() => { setStep("select"); setA(null); setOverrides({}); }} className="text-sm text-slate-500 hover:underline">← Changer de fichier</button>
            <button data-testid="publish-btn" disabled={busy || metaMissing || (!a.can_publish && Object.keys(overrides).length === 0)} onClick={() => publish(false)}
              className="inline-flex items-center gap-2 rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
              <CheckCircle2 size={16} /> {busy ? "Publication…" : "Valider et publier le dataset"}
            </button>
          </div>
          {metaMissing && <p className="text-right text-xs text-amber-600">Renseignez toutes les métadonnées obligatoires (*) avant de publier.</p>}
        </div>
      )}

      {confirmReplace && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="replace-confirm">
            <h4 className="font-heading text-base font-bold text-[#17201A]">Ce dataset existe déjà</h4>
            <p className="mt-2 text-sm text-slate-600">Un dataset avec cet identifiant existe déjà. Voulez-vous le <strong>remplacer explicitement</strong> ? Cette action recharge ses données.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button data-testid="replace-cancel" onClick={() => setConfirmReplace(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Annuler</button>
              <button data-testid="replace-confirm-btn" onClick={() => { setConfirmReplace(false); publish(true); }} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white">Remplacer</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
